import { createSelector } from 'reselect'
import moment from 'moment'
import Immutable from 'immutable'
import {useContext} from 'react';

import createOfferFilter, {
  OfferFilterDataRecord,
  filterOffer,
} from '../../../records/OfferFilter'

const getBandwidthPacks = state => state.product.get('bandwidthPacks')
const getOffers = state => state.product.get('offers')
const getProducts = state => state.product.get('products')

export const makeSelectBandwidthPack = () => createSelector(
  getBandwidthPacks,
  (state, { bandwidthPackId }) => bandwidthPackId,
  (packs, packId) => packs.find(search => search.id === packId, undefined, null),
)

export const makeSelectBandwidthPacks = () => createSelector(
  getBandwidthPacks,
  (state, { bandwidthPackIds }) => Immutable.Set(bandwidthPackIds),
  (packs, packIds) => packs.filter(search => packIds.includes(search.id)),
)

export const makeSelectOffer = () => createSelector(
  getOffers,
  (state, { offerId }) => offerId,
  (offers, id) => offers.find(offer => offer.id === id, undefined, null),
)

export const makeSelectOfferFilterData = () => {
  const selectProducts = makeSelectProducts()

  return createSelector(
    getOffers,
    selectProducts,
    getProducts,
    (state, { filter = {} } = {}) => filter,
    (state, { qualificationResults } = {}) => qualificationResults,
    (offers, products, allProducts, filter, qualificationResults) => {
      // Some offers should be excluded from the signup form entirely.
      offers = offers.filter(offer => !offer.attributes.includes('signup_hidden'));

      let filterOffers = offers.filter(offer => filterOffer(createOfferFilter({
        allowCalix: filter.allowCalix,
        hasIpn: filter.hasIpn,
        period: null,
        productId: filter.productId,
      }), offer))

      const speeds = filterOffers
        .map(offer => offer.speed)
        .filter(speed => speed)
        .toSet()
        .sort()

      const speedsWithAttributes = filterOffers
        .groupBy(offer => offer.speed)
        .map((offers, speed) => ({ speed, attributes: offers
            .flatMap(offer => offer.attributes)
            .toSet()
            .toList() }))
        .toList()

      if (filter.speed) {
        filterOffers = offers.filter(offer => filterOffer(createOfferFilter({
          allowCalix: filter.allowCalix,
          hasIpn: filter.hasIpn,
          period: null,
          productId: filter.productId,
          speed: filter.speed,
        }), offer))
      }

      const contracts = filterOffers
        .map(offer => (offer.contractLength ? offer.contractLength.asMonths() : 0))
        .toSet()
        .sort()
        .map(months => moment.duration(months, 'months'))

      if (filter.contract) {
        filterOffers = offers.filter(offer => filterOffer(createOfferFilter({
          allowCalix: filter.allowCalix,
          hasIpn: filter.hasIpn,
          period: null,
          contract: filter.contract,
          productId: filter.productId,
          speed: filter.speed,
        }), offer))
      }

      const periods = filterOffers
        .map(offer => offer.period.asMonths())
        .toSet()
        .sort()
        .map(months => moment.duration(months, 'months'))

      const disabledProducts = allProducts
        .filter(p => !products.has(p))
        .sortBy(p => p.displayOrder)

      return new OfferFilterDataRecord({
        contracts,
        disabledProducts,
        periods,
        products: products.sort(p => p.displayOrder - (Immutable.getIn(qualificationResults, [p.id], 0) * 1000)),
        speeds,
        speedsWithAttributes
      })
    },
  )
}

export const makeSelectOfferProduct = () => createSelector(
  makeSelectOffer(),
  getProducts,
  (offer, products) => products
    .find(product => product.id === offer.product, undefined, null),
)

export const makeSelectOffers = () => createSelector(
  getOffers,
  (state, { filter } = {}) => filter,
  (offers, filter) => offers
    .filter(offer => filterOffer(filter, offer))
    .sortBy(offer => offer.displayOrder),
)

export const makeSelectProduct = () => createSelector(
  getProducts,
  (state, { productId }) => productId,
  (products, id) => products.find(product => product.id === id, undefined, null),
)

export const makeSelectProducts = () => createSelector(
  getProducts,
  (state, { addressSupplied = null } = {}) => addressSupplied,
  (products, addressSupplied) => products
    .filter(product => !product.addressRequired || (addressSupplied === null || addressSupplied === true))
    .sortBy(product => product.displayOrder),
)

export const makeSelectProductsByType = () => createSelector(
  makeSelectProducts(),
  products => products
    .groupBy(product => product.get('type')),
)

export const makeSelectRequirements = () => createSelector(
  makeSelectOffer(),
  getProducts,
  (state, { requirement }) => requirement,
  (offer, products, requirement) => products
    .find(search => search.id === offer.product)
    .requirements
    .filter(search => !requirement || search.requirement === requirement),
)

export const selectBandwidthPacks = createSelector(
  getBandwidthPacks,
  packs => packs
    .sortBy(pack => pack.get('bytes')),
)

export const selectCoupons = createSelector(
  state => state.product.get('coupons'),
  coupons => coupons
    .sortBy(coupon => coupon.name),
)



// WEBPACK FOOTER //
// ./deps/internal-components/src/redux/modules/product/selectors.js